/**
 * H3 geo helpers (F6, firehose §3.3; defs.json geo). Parent derivation is pure
 * bit math on the cell index — no h3 runtime dep (tests cross-check h3-js).
 */
import { FORMATS, h3Res } from "@beacon/lexicon";
import type { SealedNeedToKnowJson } from "./seal-helpers.js";

export interface GeoJson {
  cell: string;
  path: string[];
  accuracyM?: number;
  precise?: SealedNeedToKnowJson;
}

const RES_SHIFT = 52n;
const RES_MASK = 0xfn << RES_SHIFT;

export function h3Parent(cell: string, parentRes: number): string {
  if (!FORMATS["h3-cell"](cell)) throw new Error("geo: not an h3 cell");
  const res = h3Res(cell);
  if (!Number.isInteger(parentRes) || parentRes < 0 || parentRes >= res) {
    throw new Error("geo: parent resolution must be coarser than the cell");
  }
  let v = BigInt("0x" + cell);
  v = (v & ~RES_MASK) | (BigInt(parentRes) << RES_SHIFT);
  // 7 = H3 "unused digit" marker; digit d occupies 3 bits at offset 3*(15-d).
  for (let d = parentRes + 1; d <= res; d++) v |= 7n << BigInt(3 * (15 - d));
  return v.toString(16).padStart(15, "0"); // padStart is defensive; valid cells render 15 hex chars
}

/** Res-8 cell → lexicon geo: path = parents res 7..4 descending (defs.json). */
export function buildGeo(
  cell: string,
  opts?: { accuracyM?: number; precise?: SealedNeedToKnowJson },
): GeoJson {
  if (!FORMATS["h3-cell"](cell) || h3Res(cell) !== 8) {
    throw new Error("geo: cell must be a res-8 h3 cell");
  }
  const g: GeoJson = { cell, path: [7, 6, 5, 4].map((r) => h3Parent(cell, r)) };
  if (opts?.accuracyM !== undefined) g.accuracyM = opts.accuracyM;
  if (opts?.precise !== undefined) g.precise = opts.precise;
  return g;
}
